// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { CreateAdvisoryAccountsCitecRequestBody } from '@shared/data-access/types/brokerage-types';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import { toWireOptionalIban } from '@client/banks/utils/account-reference';

/**
 * Maps {@link BankUploadForm} to the bulk upload request body.
 *
 * @remarks
 * Each account, position, and debt maps 1:1 to a flat wire row. Transactions only
 * rename fields to snake_case. Returns `CreateAdvisoryAccountsCitecRequestBody`
 * (`POST /v2/advisory/accounts/citec/`). Inverse:
 * `buildBankUploadFormFromAccountData`.
 * @param form - Validated wizard state (camelCase).
 */
export const buildBulkUploadAccountData = (
  form: BankUploadForm,
): CreateAdvisoryAccountsCitecRequestBody => ({
  institution: {
    name: form.institution.name,
    logo_url: form.institution.logoUrl,
  },
  accounts: form.accounts.map(account => ({
    iban: toWireOptionalIban(account.iban),
    name: account.name,
    currency: account.currency,
    account_type: account.accountType,
    owners: account.owners.map(owner => ({
      name: owner.name,
      role: '',
    })),
    available: account.available ?? null,
    balance_date: account.balanceDate || null,
  })),
  positions: (form.positions ?? []).map(position => ({
    iban: toWireOptionalIban(position.iban),
    name: position.name,
    isin: position.isin,
    price: position.price,
    currency: position.currency,
    quantity: position.quantity,
    asset_type: position.assetType,
    investment: position.quantity * position.price,
    acquisition_cost: position.acquisitionCost ?? null,
    snapshot_date: position.snapshotDate,
  })),
  transactions: (form.transactions ?? []).map(transaction => ({
    iban: toWireOptionalIban(transaction.iban),
    transaction_date: transaction.transactionDate,
    amount: transaction.amount,
    currency: transaction.currency,
    name: transaction.name ?? null,
    isin: transaction.isin ?? null,
    quantity: transaction.quantity ?? null,
    price: transaction.price ?? null,
    type: transaction.type ?? null,
    asset_type: transaction.assetType ?? null,
    subtype: transaction.subtype ?? null,
    description: transaction.description ?? null,
  })),
  debts: (form.debts ?? []).map(debt => ({
    iban: toWireOptionalIban(debt.iban),
    product_type: debt.productType,
    name: debt.name,
    currency: debt.currency,
    pending_amount: debt.pendingAmount,
    snapshot_date: debt.snapshotDate,
    contract_number: debt.contractNumber ?? null,
    granted_capital: debt.grantedCapital ?? null,
    interest_rate: debt.interestRate ?? null,
    limit_amount: debt.limitAmount ?? null,
    constitution_date: debt.constitutionDate ?? null,
    expiration_date: debt.expirationDate ?? null,
    next_installment: debt.nextInstallment ?? null,
    pending_receipts: debt.pendingReceipts ?? null,
    available: debt.available ?? null,
    disposed: debt.disposed ?? null,
    current_balance: debt.currentBalance ?? null,
    next_payment_date: debt.nextPaymentDate ?? null,
    amortizations: (debt.amortizations ?? []).map(amortization => ({
      installment_date: amortization.installmentDate,
      status: amortization.status ?? 'paid',
      amortized: amortization.amortized ?? null,
      interest: amortization.interest ?? null,
      total: amortization.total ?? null,
      pending_capital: amortization.pendingCapital ?? null,
    })),
  })),
});
